import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { createOrderFromCart } from "../utils/orderApi";

import Header from "../components/Header";
import Footer from "../components/Footer";
import PageTitle from "../components/PageTitle";
import { useCart } from "../context/CartContext";
import { useLanguage } from "../context/LanguageContext";
import { getProductLink } from "../utils/productLink";

export function CartPage() {
  const {
    items,
    setQty,
    removeItem,
    subtotal,
    incrementQuantity,
    decrementQuantity,
  } = useCart();
  const { token } = useAuth();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");

  // Helper function to add language prefix to paths
  const getLocalizedPath = (path) => {
    // Split path and query string
    const [pathPart, queryString] = path.split("?");

    if (language === "ar") {
      // Remove /ar if already present to avoid duplication
      const cleanPath = pathPart.startsWith("/ar")
        ? pathPart.replace("/ar", "")
        : pathPart;
      const localizedPath = `/ar${cleanPath === "/" ? "" : cleanPath}`;
      return queryString ? `${localizedPath}?${queryString}` : localizedPath;
    }
    // Remove /ar prefix if present when switching to English
    const englishPath = pathPart.startsWith("/ar")
      ? pathPart.replace("/ar", "") || "/"
      : pathPart;
    return queryString ? `${englishPath}?${queryString}` : englishPath;
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    if (!token) {
      navigate(getLocalizedPath("/account-login"));
      return;
    }
    if (!items.length) return;

    setIsCheckingOut(true);
    setCheckoutError("");

    try {
      const created = await createOrderFromCart(items, token);
      const createdId = created?.id || created?.so_id || created?.order_id;
      const createdName = created?.order_name || created?.name || "";
      if (createdId) {
        const qp = new URLSearchParams({
          createdOrderId: String(createdId),
        });
        if (createdName) qp.set("orderName", createdName);
        navigate(`${getLocalizedPath("/checkout")}?${qp.toString()}`);
      } else {
        navigate(getLocalizedPath("/checkout"));
      }
    } catch (e) {
      setCheckoutError(
        e?.message || (language === "ar" ? "فشل إنشاء الطلب. يرجى المحاولة مرة أخرى." : "Failed to create order. Please try again.")
      );
    } finally {
      setIsCheckingOut(false);
    }
  };
  return (
    <div>
      <style>
        {`
          /* Product name styling - bluish and bolder */
          .tf-cart-item_product .cart-title {
            color: #0e5aa7 !important;
            font-weight: 700 !important;
            font-size: 16px;
          }

          .tf-cart-item_product .cart-title:hover {
            color: #0a4a8a !important;
            text-decoration: underline;
          }

          /* Remove button icon styling */
          .tf-cart-item_product .remove-cart {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 8px;
            background: #fee;
            border: 1px solid #fcc;
            border-radius: 8px;
            color: #d32f2f;
            cursor: pointer;
            transition: all 0.2s ease;
            text-decoration: none;
            min-width: 36px;
            height: 36px;
          }

          .tf-cart-item_product .remove-cart:hover {
            background: #fdd;
            border-color: #f99;
            transform: scale(1.1);
          }

          .tf-cart-item_product .remove-cart:active {
            transform: scale(0.95);
          }

          .tf-cart-item_product .remove-cart svg {
            width: 18px;
            height: 18px;
          }

          /* Quantity editor styling - match CartDrawer */
          .cart-quantity .wg-quantity {
            display: inline-flex;
            align-items: center;
            background: #f8f9fa;
            border-radius: 8px;
            padding: 4px;
            border: 1px solid #e0e0e0;
            transition: all 0.2s ease;
          }

          .cart-quantity .wg-quantity:hover {
            border-color: #0e5aa7;
            background: #ffffff;
          }

          .cart-quantity .btn-quantity {
            width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #ffffff;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            font-size: 18px;
            font-weight: 600;
            color: #0e5aa7;
            transition: all 0.2s ease;
            user-select: none;
          }

          .cart-quantity .btn-quantity:hover {
            background: #0e5aa7;
            color: #ffffff;
            transform: scale(1.1);
          }

          .cart-quantity .btn-quantity:active {
            transform: scale(0.95);
          }

          .cart-quantity .wg-quantity input {
            width: 40px;
            height: 32px;
            text-align: center;
            border: none;
            background: transparent;
            font-weight: 600;
            font-size: 15px;
            color: #1a1a1a;
            padding: 0;
            margin: 0;
            line-height: 32px;
            display: inline-block;
            vertical-align: middle;
            box-sizing: border-box;
          }

          /* Total price inline styling */
          .cart-total.price {
            white-space: nowrap;
            display: inline-block;
          }

          .cart-price.price {
            white-space: nowrap;
            display: inline-block;
          }

          /* Mobile responsive styles */
          @media (max-width: 767px) {
            .tf-page-cart-wrap {
              padding: 16px;
            }

            /* Convert table to card layout on mobile */
            .tf-table-page-cart {
              display: block;
              width: 100%;
            }

            .tf-table-page-cart thead {
              display: none;
            }

            .tf-table-page-cart tbody {
              display: block;
            }

            .tf-table-page-cart tbody tr {
              display: block;
              margin-bottom: 12px;
              padding: 12px;
              border-radius: 12px;
              background: #ffffff;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
              border: 1px solid #f0f0f0;
            }

            .tf-table-page-cart tbody td {
              display: block;
              width: 100%;
              padding: 8px 0;
              border: none;
              text-align: left;
            }

            .tf-table-page-cart tbody td:first-child {
              padding-bottom: 10px;
              border-bottom: 1px solid #f0f0f0;
              overflow: visible !important;
            }

            /* Product column on mobile */
            .tf-cart-item_product {
              display: flex !important;
              flex-direction: row !important;
              align-items: flex-start !important;
              gap: 10px !important;
              width: 100% !important;
              position: relative !important;
            }

            .tf-cart-item_product .img-box {
              flex-shrink: 0 !important;
              width: 70px !important;
              height: 70px !important;
              min-width: 70px !important;
              max-width: 70px !important;
              display: block !important;
              position: relative !important;
              float: none !important;
            }

            .tf-cart-item_product .img-box img {
              width: 70px !important;
              height: 70px !important;
              object-fit: cover !important;
              border-radius: 8px !important;
              display: block !important;
              position: relative !important;
            }

            .tf-cart-item_product .cart-info {
              flex: 1 !important;
              min-width: 0 !important;
              width: auto !important;
              display: block !important;
              position: relative !important;
              margin-left: 0 !important;
              padding-left: 0 !important;
            }

            .tf-cart-item_product .cart-title {
              font-size: 14px !important;
              line-height: 1.3 !important;
              margin-bottom: 4px !important;
              word-wrap: break-word !important;
              word-break: break-word !important;
              display: block !important;
              position: relative !important;
              z-index: 1 !important;
            }

            .tf-cart-item_product .cart-meta-variant {
              font-size: 12px !important;
              color: #888 !important;
              margin-bottom: 6px !important;
              display: block !important;
              position: relative !important;
            }

            /* Price, Quantity, Total columns on mobile */
            .tf-table-page-cart tbody td[cart-data-title] {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 6px 0;
            }

            .tf-table-page-cart tbody td[cart-data-title]:before {
              content: attr(cart-data-title);
              font-weight: 600;
              font-size: 13px;
              text-transform: uppercase;
              color: #666;
              letter-spacing: 0.5px;
            }

            .tf-table-page-cart tbody td[cart-data-title] > div {
              flex-shrink: 0;
            }

            /* Quantity editor on mobile */
            .tf-cart-item_quantity .cart-quantity {
              margin-left: auto;
            }

            .tf-cart-item_quantity .wg-quantity {
              margin: 0;
            }

            /* Total price on mobile */
            .tf-cart-item_total .cart-total {
              font-size: 18px;
              font-weight: 700;
              color: #0e5aa7;
            }

            /* Cart note section */
            .tf-page-cart-note {
              margin-top: 24px;
              padding: 16px;
              background: #f8f9fa;
              border-radius: 10px;
            }

            .tf-page-cart-note label {
              display: block;
              font-weight: 600;
              margin-bottom: 8px;
              font-size: 14px;
            }

            .tf-page-cart-note textarea {
              width: 100%;
              min-height: 80px;
              padding: 12px;
              border: 1px solid #e0e0e0;
              border-radius: 8px;
              font-size: 14px;
            }

            /* Cart footer on mobile */
            .tf-page-cart-footer {
              margin-top: 24px;
              padding: 20px;
              background: #ffffff;
              border-radius: 12px;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
            }

            .tf-free-shipping-bar {
              margin-bottom: 20px;
            }

            .tf-cart-totals-discounts {
              padding: 16px 0;
              border-bottom: 2px solid #f0f0f0;
              margin-bottom: 16px;
            }

            .tf-cart-totals-discounts h3 {
              font-size: 18px;
            }

            .tf-cart-totals-discounts .total-value {
              font-size: 22px;
            }

            .cart-checkout-btn .tf-btn {
              padding: 14px 20px;
              font-size: 16px;
            }

            /* Empty cart message */
            .tf-table-page-cart tbody td.text-center {
              padding: 40px 20px;
              font-size: 16px;
            }
          }

          /* Small mobile devices */
          @media (max-width: 575px) {
            .tf-page-cart-wrap {
              padding: 12px;
            }

            .tf-table-page-cart tbody tr {
              padding: 12px;
            }

            .tf-cart-item_product .cart-title {
              font-size: 14px;
            }

            .tf-table-page-cart tbody td[cart-data-title]:before {
              font-size: 12px;
              min-width: 60px;
            }
          }
        `}
      </style>
      <Header />
      <PageTitle title={language === "ar" ? "سلة التسوق" : "Shopping Cart"} />

      {/* Your HTML content here */}
      <section className="flat-spacing-11">
        <div className="container">
          {/* The cart countdown section */}
          <div className="tf-cart-countdown">
            <div className="title-left">
              <svg
                className="d-inline-block"
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="24"
                viewBox="0 0 16 24"
                fill="rgb(219 18 21)"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M10.0899 24C11.3119 22.1928 11.4245 20.2409 10.4277 18.1443C10.1505 19.2691 9.64344 19.9518 8.90645 20.1924C9.59084 18.2379 9.01896 16.1263 7.19079 13.8576C7.15133 16.2007 6.58824 17.9076 5.50148 18.9782C4.00436 20.4517 4.02197 22.1146 5.55428 23.9669C-0.806588 20.5819 -1.70399 16.0418 2.86196 10.347C3.14516 11.7228 3.83141 12.5674 4.92082 12.8809C3.73335 7.84186 4.98274 3.54821 8.66895 0C8.6916 7.87426 11.1062 8.57414 14.1592 12.089C17.4554 16.3071 15.5184 21.1748 10.0899 24Z"
                />
              </svg>
              <p>{language === "ar" ? "هذه الأسعار محدودة، أتمم الشراء بسرعة!!" : "These prices are limited, checkout fast!!"} </p>
            </div>
            {/* <div
              className="js-countdown timer-count"
              data-timer="600"
              data-labels="d:,h:,m:,s"
            ></div> */}
          </div>

          {/* Cart items */}
          <div className="tf-page-cart-wrap">
            <div className="tf-page-cart-item">
              <form>
                <table className="tf-table-page-cart">
                  <thead>
                    <tr>
                      <th>{language === "ar" ? "المنتج" : "Product"}</th>
                      <th>{language === "ar" ? "السعر" : "Price"}</th>
                      <th>{language === "ar" ? "الكمية" : "Quantity"}</th>
                      <th>{language === "ar" ? "الإجمالي" : "Total"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center">
                          {language === "ar" ? "سلة التسوق فارغة." : "Your cart is empty."}
                        </td>
                      </tr>
                    ) : (
                      items.map((it) => (
                        <tr key={it.id} className="tf-cart-item file-delete">
                          <td className="tf-cart-item_product">
                            <Link
                              to={getProductLink(it)}
                              state={{ product: it }}
                              className="img-box"
                            >
                              <img src={it.image} alt={it.name} />
                            </Link>
                            <div className="cart-info">
                              <Link
                                to={getProductLink(it)}
                                state={{ product: it }}
                                style={{ color: "#0e5aa7" }}
                                className="cart-title link"
                              >
                                {it.name}
                              </Link>
                              <div className="cart-meta-variant">
                                {it.color || "-"}
                                {it.size ? ` / ${it.size}` : ""}
                              </div>
                              <button
                                type="button"
                                className="remove-cart link"
                                onClick={() => removeItem(it.id)}
                                aria-label={language === "ar" ? "إزالة العنصر" : "Remove item"}
                              >
                                <svg
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.6"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  style={{ width: "18px", height: "18px" }}
                                >
                                  <path d="M3 6h18M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2m-8 0h8m-7 4 1 10m6-10-1 10M5 6l1.5 16a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2L19 6" />
                                </svg>
                              </button>
                            </div>
                          </td>
                          <td
                            className="tf-cart-item_price tf-variant-item-price"
                            cart-data-title={language === "ar" ? "السعر" : "Price"}
                          >
                            <div className="cart-price price">
                              EGP {(it.price || 0).toFixed(2)}
                            </div>
                          </td>
                          <td
                            className="tf-cart-item_quantity"
                            cart-data-title={language === "ar" ? "الكمية" : "Quantity"}
                          >
                            <div className="cart-quantity">
                              <div className="wg-quantity">
                                <span
                                  className="btn-quantity"
                                  onClick={() => decrementQuantity(it.id)}
                                >
                                  -
                                </span>
                                <input
                                  type="text"
                                  value={Number(it.qty || 1)}
                                  readOnly
                                />
                                <span
                                  className="btn-quantity"
                                  onClick={() => incrementQuantity(it.id)}
                                >
                                  +
                                </span>
                              </div>
                            </div>
                          </td>
                          <td
                            className="tf-cart-item_total tf-variant-item-total"
                            cart-data-title={language === "ar" ? "الإجمالي" : "Total"}
                          >
                            <div className="cart-total price">
                              EGP {((it.price || 0) * (it.qty || 1)).toFixed(2)}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>

                {/* Cart Note Section */}
                <div className="tf-page-cart-note">
                  <label for="cart-note">{language === "ar" ? "إضافة ملاحظة للطلب" : "Add Order Note"}</label>
                  <textarea
                    name="note"
                    id="cart-note"
                    placeholder={language === "ar" ? "كيف يمكننا مساعدتك؟" : "How can we help you?"}
                  ></textarea>
                </div>
              </form>
            </div>

            {/* Cart Footer */}
            <div className="tf-page-cart-footer">
              <div className="tf-cart-footer-inner">
                <div className="tf-free-shipping-bar">
                  <div className="tf-progress-bar">
                    <span style={{ width: "50%" }}>
                      <div className="progress-car">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="21"
                          height="14"
                          viewBox="0 0 21 14"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M0 0.875C0 0.391751 0.391751 0 0.875 0H13.5625C14.0457 0 14.4375 0.391751 14.4375 0.875V3.0625H17.3125C17.5867 3.0625 17.845 3.19101 18.0104 3.40969L20.8229 7.12844C20.9378 7.2804 21 7.46572 21 7.65625V11.375C21 11.8582 20.6082 12.25 20.125 12.25H17.7881C17.4278 13.2695 16.4554 14 15.3125 14C14.1696 14 13.1972 13.2695 12.8369 12.25H7.72563C7.36527 13.2695 6.39293 14 5.25 14C4.10706 14 3.13473 13.2695 2.77437 12.25H0.875C0.391751 12.25 0 11.8582 0 11.375V0.875ZM2.77437 10.5C3.13473 9.48047 4.10706 8.75 5.25 8.75C6.39293 8.75 7.36527 9.48046 7.72563 10.5H12.6875V1.75H1.75V10.5H2.77437ZM14.4375 8.89937V4.8125H16.8772L19.25 7.94987V10.5H17.7881C17.4278 9.48046 16.4554 8.75 15.3125 8.75C15.0057 8.75 14.7112 8.80264 14.4375 8.89937ZM5.25 10.5C4.76676 10.5 4.375 10.8918 4.375 11.375C4.375 11.8582 4.76676 12.25 5.25 12.25C5.73323 12.25 6.125 11.8582 6.125 11.375C6.125 10.8918 5.73323 10.5 5.25 10.5ZM15.3125 10.5C14.8293 10.5 14.4375 10.8918 14.4375 11.375C14.4375 11.8582 14.8293 12.25 15.3125 12.25C15.7957 12.25 16.1875 11.8582 16.1875 11.375C16.1875 10.8918 15.7957 10.5 15.3125 10.5Z"
                          />
                        </svg>
                      </div>
                    </span>
                  </div>
                  <div className="tf-progress-msg">
                    {language === "ar" ? (
                      <>
                        أكد طلبك وادفع الآن واستمتع{" "}
                        <span className="fw-6">بشحن سريع</span>
                      </>
                    ) : (
                      <>
                        Confirm your order and pay now and enjoy{" "}
                        <span className="fw-6">Fast Shipping</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="tf-page-cart-checkout">
                  <div className="tf-cart-totals-discounts">
                    <h3>{language === "ar" ? "المجموع الفرعي" : "Subtotal"}</h3>
                    <span className="total-value">
                      EGP {(subtotal || 0).toFixed(2)}
                    </span>
                  </div>
                  <p className="tf-cart-tax">
                    {language === "ar" ? (
                      <>
                        الضرائب و<a href="shipping-delivery.html">الشحن</a>{" "}
                        تُحسب عند الدفع
                      </>
                    ) : (
                      <>
                        Taxes and <a href="shipping-delivery.html">shipping</a>{" "}
                        calculated at checkout
                      </>
                    )}
                  </p>
                  <div className="cart-checkbox">
                    <input
                      type="checkbox"
                      className="tf-check"
                      id="check-agree"
                      defaultChecked
                    />
                    <label for="check-agree" className="fw-4">
                      {language === "ar" ? (
                        <>
                          أوافق على{" "}
                          <a href="terms-conditions.html">الشروط والأحكام</a>
                        </>
                      ) : (
                        <>
                          I agree with the{" "}
                          <a href="terms-conditions.html">terms and conditions</a>
                        </>
                      )}
                    </label>
                  </div>
                  {checkoutError && (
                    <div className="alert alert-danger mb-3" role="alert">
                      {checkoutError}
                    </div>
                  )}
                  <div className="cart-checkout-btn">
                    <button
                      type="button"
                      onClick={handleCheckout}
                      disabled={isCheckingOut}
                      className="tf-btn w-100 btn-fill animate-hover-btn radius-3 justify-content-center"
                    >
                      <span>
                        {isCheckingOut
                          ? (language === "ar" ? "جاري إنشاء الطلب..." : "Creating Order...")
                          : (language === "ar" ? "الدفع" : "Check out")}
                      </span>
                    </button>
                  </div>
                  <div className="tf-page-cart_imgtrust">
                    <p className="text-center fw-6">{language === "ar" ? "ضمان الدفع الآمن" : "Guarantee Safe Checkout"}</p>
                    <div className="cart-list-social">
                      <div className="payment-item">
                        <svg
                          viewBox="0 0 38 24"
                          xmlns="http://www.w3.org/2000/svg"
                          role="img"
                          width="38"
                          height="24"
                        >
                          <title id="pi-visa">Visa</title>
                          {/* Visa icon here */}
                        </svg>
                      </div>
                      <div className="payment-item">
                        <svg
                          viewBox="0 0 38 24"
                          xmlns="http://www.w3.org/2000/svg"
                          role="img"
                          width="38"
                          height="24"
                        >
                          <title id="pi-paypal">PayPal</title>
                          {/* PayPal icon here */}
                        </svg>
                      </div>
                      <div className="payment-item">
                        <svg
                          viewBox="0 0 38 24"
                          xmlns="http://www.w3.org/2000/svg"
                          role="img"
                          width="38"
                          height="24"
                        >
                          <title id="pi-master">Mastercard</title>
                          {/* Mastercard icon here */}
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default CartPage;
